import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Container, Typography, Button } from "@mui/material";
import { IoArrowForward, IoChatbubbleEllipsesOutline } from "react-icons/io5";
import { adminColors, TOUCH_HIT_SX } from "../theme/adminTheme";
import { FONT_STACK, BORDER_COLOR, BrandHeader } from "./legal/LegalPageLayout";

/**
 * Catch-all 404. Deliberately small and quiet - a wrong/stale link is a
 * routine dead end, not an incident, so this skips the gigantic hero /
 * illustration treatment the rest of the marketing surface uses and just
 * gets the reader back on track. Shares the same header + card shell as
 * the legal pages (BrandHeader, adminColors, FONT_STACK) rather than a
 * one-off look, and its card/typography react the same at 375-1440px since
 * it's the same MUI Container/Box primitives everything else here uses.
 */
export default function NotFoundPage() {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: adminColors.surface,
        fontFamily: FONT_STACK,
        "@media (prefers-reduced-motion: reduce)": {
          "*": {
            transitionDuration: "0.01ms !important",
            animationDuration: "0.01ms !important",
          },
        },
      }}
    >
      <BrandHeader />

      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          py: { xs: 6, sm: 8 },
        }}
      >
        <Container maxWidth="xs">
          <Box
            sx={{
              bgcolor: "white",
              border: `1px solid ${BORDER_COLOR}`,
              borderRadius: "14px",
              px: { xs: 3, sm: 4.5 },
              py: { xs: 4, sm: 5 },
              textAlign: "center",
            }}
          >
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: "0.8rem",
                letterSpacing: "0.08em",
                color: adminColors.accent,
                mb: 1.5,
              }}
            >
              404
            </Typography>

            <Typography
              component="h1"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "1.4rem", sm: "1.6rem" },
                color: adminColors.ink,
                letterSpacing: "-0.01em",
                lineHeight: 1.25,
                mb: 1,
              }}
            >
              We couldn&apos;t find that page
            </Typography>

            <Typography
              sx={{
                fontSize: "0.95rem",
                color: adminColors.inkSoft,
                lineHeight: 1.6,
                mb: 3.5,
              }}
            >
              The link may be out of date, or the page may have moved.
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
              <Button
                variant="contained"
                disableElevation
                onClick={() => navigate("/")}
                endIcon={<IoArrowForward size={15} />}
                sx={{
                  bgcolor: adminColors.accent,
                  fontWeight: 600,
                  py: 1.1,
                  "&:hover": { bgcolor: adminColors.accentDark },
                  ...TOUCH_HIT_SX,
                }}
              >
                Go home
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate("/chat")}
                startIcon={<IoChatbubbleEllipsesOutline size={16} />}
                sx={{
                  borderColor: BORDER_COLOR,
                  color: adminColors.ink,
                  fontWeight: 600,
                  py: 1.1,
                  "&:hover": {
                    borderColor: adminColors.accent,
                    bgcolor: adminColors.accentLight,
                  },
                  ...TOUCH_HIT_SX,
                }}
              >
                Open chat
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
